import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import Header from '../components/Header';
import { BUILTIN_TEMPLATES } from '../data/builtinTemplates';

function StatusBadge({ status }) {
  if (!status) return <span className="text-gray-600 text-xs">—</span>;
  const styles = {
    pass: 'bg-green-500/20 text-green-400',
    fail: 'bg-red-500/20 text-red-400',
    na: 'bg-gray-600/30 text-gray-400',
  };
  const labels = { pass: 'PASS', fail: 'FAIL', na: 'N/A' };
  return (
    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${styles[status] || 'text-gray-400'}`}>
      {labels[status] || status.toUpperCase()}
    </span>
  );
}

function itemKey(sIdx, iIdx) {
  return `s${sIdx}_i${iIdx}`;
}

export default function SubmissionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    async function load() {
      const { data, error } = await supabase
        .from('submissions')
        .select('*, assets(name, identifier), checklist_templates(name, items)')
        .eq('id', id)
        .single();

      if (error || !data) { setLoading(false); return; }
      setSubmission(data);

      if (BUILTIN_TEMPLATES[data.checklist_type]) {
        setSections(BUILTIN_TEMPLATES[data.checklist_type].sections);
      } else if (data.checklist_templates?.items) {
        setSections(data.checklist_templates.items);
      }

      setLoading(false);
    }
    load();
  }, [id]);

  const handleExport = async () => {
    if (!submission || sections.length === 0) return;
    setExporting(true);
    try {
      const { jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;
      const doc = new jsPDF();

      const assetLabel = submission.assets
        ? `${submission.assets.name}${submission.assets.identifier ? ` (${submission.assets.identifier})` : ''}`
        : 'N/A';

      // Header bar
      doc.setFillColor(30, 30, 40);
      doc.rect(0, 0, 210, 30, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('GroundCheck', 14, 13);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Safety Inspection Report', 14, 22);

      // Details
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Checklist: ${submission.checklist_type?.replace(/_/g, ' ')}`, 14, 42);
      doc.text(`Operator: ${submission.driver_name || 'N/A'}`, 14, 49);
      doc.text(`Vehicle/Asset: ${assetLabel}`, 14, 56);
      doc.text(`Date: ${new Date(submission.created_at).toLocaleString('en-AU')}`, 14, 63);
      doc.text(`Odometer: ${submission.start_odometer ? submission.start_odometer + ' km' : 'N/A'}`, 14, 70);

      // Build rows from sections
      const rows = [];
      sections.forEach((section, sIdx) => {
        section.items.forEach((item, iIdx) => {
          const key = itemKey(sIdx, iIdx);
          const r = submission.responses?.[key] || {};
          rows.push([
            `[${section.title}] ${item}`,
            r.status?.toUpperCase() || '—',
            r.notes || '',
          ]);
        });
      });

      autoTable(doc, {
        startY: 78,
        head: [['Item', 'Result', 'Notes']],
        body: rows,
        headStyles: { fillColor: [249, 115, 22], textColor: 255, fontStyle: 'bold', fontSize: 9 },
        bodyStyles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 95 },
          1: { cellWidth: 25, halign: 'center' },
          2: { cellWidth: 65 },
        },
        didParseCell: (data) => {
          if (data.column.index === 1 && data.section === 'body') {
            const val = data.cell.raw;
            if (val === 'PASS') data.cell.styles.textColor = [34, 197, 94];
            if (val === 'FAIL') data.cell.styles.textColor = [239, 68, 68];
            if (val === 'N/A') data.cell.styles.textColor = [150, 150, 150];
          }
        },
        alternateRowStyles: { fillColor: [248, 248, 248] },
      });

      // Defects summary
      const defects = Array.isArray(submission.defects) ? submission.defects : [];
      if (defects.length > 0) {
        const finalY = doc.lastAutoTable.finalY + 10;
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('Defects Recorded:', 14, finalY);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        defects.forEach((d, i) => {
          doc.text(
            `${i + 1}. [${d.section}] ${d.item}${d.notes ? ' — ' + d.notes : ''}`,
            14,
            finalY + 8 + (i * 7)
          );
        });
      }

      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text('Generated by GroundCheck — groundcheck.io', 14, 285);
      doc.save(`groundcheck-${submission.checklist_type}-${new Date(submission.created_at).toLocaleDateString('en-AU')}.pdf`);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-orange-500 animate-pulse">Loading…</div>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-red-400 mb-2">Submission not found</p>
          <button onClick={() => navigate('/submissions')} className="text-orange-400 text-sm">
            Back to submissions
          </button>
        </div>
      </div>
    );
  }

  const defects = Array.isArray(submission.defects) ? submission.defects : [];
  const assetDisplay = submission.assets
    ? `${submission.assets.name}${submission.assets.identifier ? ` (${submission.assets.identifier})` : ''}`
    : '—';

  return (
    <div className="min-h-screen bg-gray-900 pb-24">
      <Header title="Submission Detail" backTo="/submissions" />

      <div className="max-w-lg mx-auto px-4 py-5 space-y-4">
        {/* Summary card */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-5">
          <div className="mb-4">
            <p className="text-gray-400 text-xs mb-0.5">Checklist</p>
            <p className="text-white font-semibold capitalize">
              {submission.checklist_type?.replace(/_/g, ' ')}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-gray-500 text-xs">Operator</p>
              <p className="text-white">{submission.driver_name || '—'}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs">Vehicle / Asset</p>
              <p className="text-white">{assetDisplay}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs">Date & Time</p>
              <p className="text-white">{new Date(submission.created_at).toLocaleString('en-AU')}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs">Odometer</p>
              <p className="text-white">{submission.start_odometer ? `${submission.start_odometer} km` : '—'}</p>
            </div>
          </div>
        </div>

        {/* Defects */}
        {defects.length > 0 && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
            <h3 className="text-red-400 font-semibold text-sm mb-3">
              {defects.length} Defect{defects.length !== 1 ? 's' : ''} Recorded
            </h3>
            <div className="space-y-2">
              {defects.map((d, i) => (
                <div key={i} className="text-sm">
                  <p className="text-red-300 font-medium">{d.item}</p>
                  <p className="text-red-400/70 text-xs">{d.section}</p>
                  {d.notes && <p className="text-gray-400 text-xs mt-0.5">{d.notes}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Checklist results by section */}
        {sections.map((section, sIdx) => (
          <div key={sIdx} className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
            <div className="bg-gray-700/50 px-4 py-2.5">
              <h3 className="text-orange-400 font-semibold text-xs uppercase tracking-wide">
                {section.title}
              </h3>
            </div>
            <div className="divide-y divide-gray-700">
              {section.items.map((item, iIdx) => {
                const key = itemKey(sIdx, iIdx);
                const r = submission.responses?.[key] || {};
                return (
                  <div key={key} className={`px-4 py-3 ${r.status === 'fail' ? 'bg-red-500/5' : ''}`}>
                    <div className="flex items-start gap-3">
                      <p className={`flex-1 text-sm leading-snug ${r.status === 'fail' ? 'text-red-300' : 'text-gray-300'}`}>
                        {item}
                      </p>
                      <StatusBadge status={r.status} />
                    </div>
                    {r.notes && (
                      <p className="text-gray-500 text-xs mt-1.5 italic">{r.notes}</p>
                    )}
                    {r.photo && (
                      <img
                        src={r.photo}
                        alt="Evidence"
                        className="mt-2 w-20 h-20 object-cover rounded-lg border border-gray-600"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Export button */}
      <div className="fixed bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-700 px-4 py-4 z-10">
        <div className="max-w-lg mx-auto">
          <button
            onClick={handleExport}
            disabled={exporting || sections.length === 0}
            className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold py-4 rounded-xl transition-colors"
          >
            {exporting ? 'Generating PDF…' : 'Export PDF Report'}
          </button>
        </div>
      </div>
    </div>
  );
}
